import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Button } from 'primereact/button'

import { Header } from '../../../lib/header'
import Text from '../../../ui/Text'
import { CurrentUser } from '../../../lib/currentUser'
import { WidgetLayout } from '../../../lib/widget-layout'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { ChargePoint } from '../../../common/redux/api/web-api'
import isOnline from '../../../lib/isOnline'
import useText from '../../../common/hooks/useText'
import Tooltip from '../../../common/components/Tooltip'
import { InfoIcon } from '../../../lib/icons/info-icon'

import { AddChargePointDialog } from './dialogs/add-charge-point-dialog'

const StatusIcon = styled.span`
    margin-left: 10px;
`

export type UserProfileChargepointListProps = {
    className?: ClassName
    style?: React.CSSProperties
    chargePoints?: ChargePoint[]
    removeChargePoint: (id: string) => void
    editChargePoint: (chargePoint: ChargePoint) => void
    loading: boolean
    user: CurrentUser
}

export function UserProfileChargepointList({
    className,
    style,
    chargePoints,
    loading,
    removeChargePoint,
    editChargePoint,
    user,
}: UserProfileChargepointListProps): React.ReactElement {
    const text = useText()
    const [showAddChargePoint, setShowAddChargePoint] = useState(false)

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={<Header code="lblMyChargersWidget" />}
            data-testid={'myChargers'}
            headerButtons={
                <Button
                    onClick={() => setShowAddChargePoint(true)}
                    data-testid="showAddChargePoint"
                    label={text('btnAdd')}
                    icon="pi pi-plus"
                />
            }
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <Text code="lblStatus" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="serialNumber">
                            <Text code="lblSerialNumber" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="name">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell />
                    </TableRow>
                </TableHeader>
                {!loading && (
                    <TableBody>
                        {chargePoints &&
                            chargePoints.map((chargePoint) => (
                                <TableRow key={chargePoint.id} className="clickable">
                                    <TableBodyCell>
                                        <StatusIcon
                                            className={cn(
                                                'fa fa-wifi',
                                                isOnline(chargePoint) ? 'greenFont' : 'redFont'
                                            )}
                                            title={text(isOnline(chargePoint) ? 'lblOnline' : 'lblOffline')}
                                        />
                                    </TableBodyCell>
                                    <TableBodyCell>{chargePoint.id}</TableBodyCell>
                                    <TableBodyCell data-testid={`name-${chargePoint.name}`}>
                                        {chargePoint.name}
                                    </TableBodyCell>

                                    <TableBodyCell>
                                        <div className="flex justify-content-end gap-2">
                                            <Button
                                                size="small"
                                                icon="pi pi-pencil"
                                                rounded
                                                outlined
                                                severity="info"
                                                aria-label={text('lblEditChargerName')}
                                                onClick={() => {
                                                    editChargePoint(chargePoint)
                                                }}
                                                data-testid="edit-button"
                                            />

                                            {!chargePoint.ownerReadOnly &&
                                                !chargePoint.companyId &&
                                                chargePoint.ownerId && (
                                                    <Button
                                                        size="small"
                                                        icon="pi pi-trash"
                                                        rounded
                                                        outlined
                                                        severity="danger"
                                                        onClick={() => {
                                                            removeChargePoint(chargePoint.id)
                                                        }}
                                                        data-testid="remove-button"
                                                    />
                                                )}
                                            {chargePoint.ownerReadOnly && (
                                                <Tooltip content="msgChargerCanOnlyBeModifiedByAdministrators">
                                                    <InfoIcon size={25} data-testid="tooltipIcon" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                    </TableBody>
                )}
                {loading && <div className="shimmer-container shimmer-xsmall margin-bottom-small" />}
            </Table>
            <AddChargePointDialog
                visible={showAddChargePoint}
                onHide={() => setShowAddChargePoint(false)}
                userId={user.id}
            />
        </WidgetLayout>
    )
}
